import type { VariantState } from "./variantState";
import type { Reducer } from "react";

type SelectVariantAction = {
  payload: { variantId: number };
  type: "variant/select";
};
type IncreaseVariantQuantityAction = {
  payload: { variantId: number };
  type: "variant/increase";
};
type DecreaseVariantQuantityAction = {
  payload: { variantId: number };
  type: "variant/decrease";
};
type ChangeVariantQuantityAction = {
  payload: { nextAmount: number; variantId: number };
  type: "variant/change";
};
type Action =
  | ChangeVariantQuantityAction
  | DecreaseVariantQuantityAction
  | IncreaseVariantQuantityAction
  | SelectVariantAction;

export const selectVariant = (variantId: number): SelectVariantAction => ({
  payload: { variantId },
  type: "variant/select",
});

export const increaseVariantQuantity = (
  variantId: number
): IncreaseVariantQuantityAction => ({
  payload: { variantId },
  type: "variant/increase",
});

export const decreaseVariantQuantity = (
  variantId: number
): DecreaseVariantQuantityAction => ({
  payload: { variantId },
  type: "variant/decrease",
});

export const changeVariantQuantity = (
  variantId: number,
  nextAmount: number
): ChangeVariantQuantityAction => ({
  payload: { nextAmount, variantId },
  type: "variant/change",
});

const cloneState = (prevState: VariantState): VariantState => ({
  ...prevState,
  amounts: new Map(prevState.amounts),
});

export const variantReducer: Reducer<VariantState, Action> = (
  prevState,
  action
) => {
  const {
    type,
    payload,
    payload: { variantId },
  } = action;

  switch (type) {
    case "variant/increase": {
      const currentCount = prevState.amounts.get(variantId);
      if (currentCount === undefined) return prevState;

      const newState = cloneState(prevState);
      newState.amounts.set(variantId, currentCount + 1);
      return newState;
    }

    case "variant/decrease": {
      const currentCount = prevState.amounts.get(variantId);
      if (currentCount === undefined || currentCount === 0) return prevState;

      const newState = cloneState(prevState);
      newState.amounts.set(variantId, currentCount - 1);
      return newState;
    }

    case "variant/select": {
      const newState = cloneState(prevState);

      if (newState.amounts.get(variantId) === undefined) {
        newState.amounts.set(variantId, 0);
      }
      newState.selectedVariantId = variantId;
      return newState;
    }

    case "variant/change": {
      const { nextAmount } = payload;

      const newState = cloneState(prevState);
      newState.amounts.set(variantId, nextAmount);
      return newState;
    }

    default: {
      const _: never = type;
      return prevState;
    }
  }
};
